// Spec and change notice text. People write in the formatted editor (RichEditor.tsx, loaded on first use: the page
// shows the rendered text meanwhile, so nothing jumps); the text is stored as Markdown, which vendors see through the
// same renderer. "Markdown" switches to the source for people who know it (remembered on this computer), with a
// toolbar and image paste/drop. Ctrl/⌘+S saves; leaving the text saves too. Read-only shows the rendered text.
import { AlertTriangle, Bold, Heading2, ImagePlus, Info, Pilcrow, Table } from "lucide-react";
import { type ClipboardEvent, type DragEvent, lazy, Suspense, useMemo, useRef, useState } from "react";
import { renderMarkdown, withoutEmptySections } from "../../../../shared/markdown.ts";
import { cx } from "../../ui/controls.tsx";
import { toast } from "../../ui/toast.tsx";
import { uploadSpecImage } from "./lib.ts";

export type TextUpdate = string | ((prev: string) => string);

const RichEditor = lazy(() => import("./RichEditor.tsx"));

export function MarkdownPreview({ source, className, empty = "Nothing written yet." }: { source: string; className?: string; empty?: string }) {
  // Without the starting template's empty sections (withoutEmptySections()), as vendors read it
  const html = useMemo(() => renderMarkdown(withoutEmptySections(source)), [source]);
  if (!source.trim()) return <div className={cx("text-faint", className)}>{empty}</div>;
  // biome-ignore lint/security/noDangerouslySetInnerHtml: renderMarkdown escapes raw HTML and only allows safe links and images
  return <div className={cx("md", className)} dangerouslySetInnerHTML={{ __html: html }} />;
}

const SOURCE_KEY = "hhl.specs.markdownSource";
const readSourcePref = () => {
  try {
    return localStorage.getItem(SOURCE_KEY) === "1";
  } catch {
    return false;
  }
};

export function MarkdownEditor({
  value,
  onChange,
  onSave,
  readOnly,
  placeholder,
  className,
  lang,
}: {
  value: string;
  onChange: (next: TextUpdate) => void;
  onSave?: () => void;
  readOnly?: boolean;
  /** Kept for callers; the editor fills whatever width it has. */
  wide?: boolean;
  placeholder?: string;
  className?: string;
  lang?: string;
}) {
  const [source, setSource] = useState(readSourcePref);
  const choose = (on: boolean) => {
    setSource(on);
    try {
      localStorage.setItem(SOURCE_KEY, on ? "1" : "0");
    } catch {}
  };

  if (readOnly)
    return (
      <div className={cx("overflow-y-auto rounded-md border border-line bg-surface px-4 py-3", className)}>
        <MarkdownPreview source={value} />
      </div>
    );
  if (source)
    return (
      <SourceEditor
        value={value}
        onChange={onChange}
        onSave={onSave}
        placeholder={placeholder}
        lang={lang}
        className={className}
        onFormatted={() => choose(false)}
      />
    );
  return (
    <Suspense
      fallback={
        <div className={cx("flex min-h-0 flex-col rounded-md border border-line bg-surface", className)}>
          <div className="h-[37px] border-b border-line" />
          <MarkdownPreview source={value} className="min-h-0 flex-1 overflow-y-auto px-4 py-3" empty="" />
        </div>
      }
    >
      <RichEditor value={value} onChange={onChange} onSave={onSave} onSource={() => choose(true)} placeholder={placeholder} lang={lang} className={className} />
    </Suspense>
  );
}

const imagesIn = (data: DataTransfer | null) => [...(data?.files ?? [])].filter((f) => f.type.startsWith("image/"));

/** The Markdown itself, in a text box: for people who know Markdown. */
function SourceEditor({
  value,
  onChange,
  onSave,
  placeholder,
  lang,
  className,
  onFormatted,
}: {
  value: string;
  onChange: (next: TextUpdate) => void;
  onSave?: () => void;
  placeholder?: string;
  lang?: string;
  className?: string;
  onFormatted: () => void;
}) {
  const area = useRef<HTMLTextAreaElement>(null);

  /** Replaces the selection with `text` (keeping undo where the browser allows), then selects `select` within it. */
  const insert = (text: string, select?: [number, number]) => {
    const el = area.current;
    if (!el) return onChange((prev) => prev + text);
    el.focus();
    const start = el.selectionStart;
    // execCommand keeps the browser's undo history; fall back to a plain splice.
    if (!document.execCommand("insertText", false, text)) onChange((prev) => prev.slice(0, start) + text + prev.slice(el.selectionEnd));
    if (select) requestAnimationFrame(() => el.setSelectionRange(start + select[0], start + select[1]));
  };

  const wrap = (before: string, after: string, fallback: string) => {
    const el = area.current;
    const picked = el ? el.value.slice(el.selectionStart, el.selectionEnd) : "";
    const inner = picked || fallback;
    insert(before + inner + after, [before.length, before.length + inner.length]);
  };

  const block = (text: string, select?: [number, number]) => {
    const el = area.current;
    const atLineStart = !el || el.selectionStart === 0 || el.value[el.selectionStart - 1] === "\n";
    const lead = atLineStart ? "" : "\n\n";
    insert(lead + text, select ? [select[0] + lead.length, select[1] + lead.length] : undefined);
  };

  const addImages = async (files: File[]) => {
    for (const file of files) {
      const tag = `![Uploading ${file.name || "image"}…]()`;
      insert(`${tag}\n`);
      try {
        const url = await uploadSpecImage(file);
        const alt = (file.name || "").replace(/\.[a-z0-9]+$/i, "").replace(/^image$/, "");
        onChange((prev) => prev.replace(tag, `![${alt}](${url})`));
      } catch (e) {
        onChange((prev) => prev.replace(`${tag}\n`, ""));
        toast.error((e as Error).message);
      }
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const images = imagesIn(e.clipboardData);
    if (!images.length) return;
    e.preventDefault();
    addImages(images);
  };
  const onDrop = (e: DragEvent) => {
    const images = imagesIn(e.dataTransfer);
    if (!images.length) return;
    e.preventDefault();
    e.stopPropagation();
    const el = area.current;
    // Put the image roughly where it was dropped.
    if (el && "caretPositionFromPoint" in document) {
      const pos = (document as Document & { caretPositionFromPoint(x: number, y: number): { offset: number } | null }).caretPositionFromPoint(
        e.clientX,
        e.clientY,
      );
      if (pos) el.setSelectionRange(pos.offset, pos.offset);
    }
    addImages(images);
  };

  const picker = useRef<HTMLInputElement>(null);
  const tools = [
    { icon: <Heading2 className="size-3.5" />, title: "Heading", run: () => block("## ", [3, 3]) },
    { icon: <Bold className="size-3.5" />, title: "Bold (Ctrl+B)", run: () => wrap("**", "**", "bold") },
    { icon: <Table className="size-3.5" />, title: "Table", run: () => block("| Specification | Value |\n|---|---|\n|  |  |\n", [2, 15]) },
    { icon: <AlertTriangle className="size-3.5" />, title: "Warning callout", run: () => block(":::warning Title\nWhat they must do.\n:::\n", [11, 16]) },
    { icon: <Info className="size-3.5" />, title: "Info callout", run: () => block(":::info\nA note.\n:::\n", [8, 15]) },
    { icon: <ImagePlus className="size-3.5" />, title: "Image (or paste / drop one)", run: () => picker.current?.click() },
  ];

  return (
    <div className={cx("flex min-h-0 flex-col", className)}>
      <div className="flex items-center gap-1 pb-1.5">
        {tools.map((t) => (
          <button
            key={t.title}
            type="button"
            title={t.title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={t.run}
            className="rounded p-1 text-muted hover:bg-hover hover:text-text"
          >
            {t.icon}
          </button>
        ))}
        <input
          ref={picker}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            addImages([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={onFormatted}
          title="Back to the formatted editor"
          className="ml-auto flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-accent-text hover:bg-hover"
        >
          <Pilcrow className="size-3.5" /> Formatted
        </button>
      </div>
      <textarea
        ref={area}
        lang={lang}
        value={value}
        spellCheck
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        onDrop={onDrop}
        onBlur={() => onSave?.()}
        onKeyDown={(e) => {
          const mod = e.metaKey || e.ctrlKey;
          if (mod && e.key.toLowerCase() === "s") {
            e.preventDefault();
            onSave?.();
          } else if (mod && e.key.toLowerCase() === "b") {
            e.preventDefault();
            wrap("**", "**", "bold");
          }
        }}
        className="h-full min-h-0 w-full flex-1 resize-none rounded-md border border-line bg-surface px-3 py-2 font-mono text-[12.5px] leading-relaxed placeholder:text-faint hover:border-line-strong focus:border-accent focus:outline-none"
      />
    </div>
  );
}
